/**
 * Firebase configuration + the master on/off switch, read from VITE_ env vars.
 * Secrets are NEVER hardcoded — see .env.example. If the flag is on but config
 * is incomplete, we degrade to the local backend (and warn) rather than crash.
 */

export interface FirebaseEnv {
  apiKey: string;
  authDomain: string;
  projectId: string;
  storageBucket: string;
  messagingSenderId: string;
  appId: string;
  databaseURL: string;
}

const env = import.meta.env;

function read(value: string | undefined): string {
  return (value ?? '').trim();
}

export const firebaseConfig: FirebaseEnv = {
  apiKey: read(env.VITE_FIREBASE_API_KEY),
  authDomain: read(env.VITE_FIREBASE_AUTH_DOMAIN),
  projectId: read(env.VITE_FIREBASE_PROJECT_ID),
  storageBucket: read(env.VITE_FIREBASE_STORAGE_BUCKET),
  messagingSenderId: read(env.VITE_FIREBASE_MESSAGING_SENDER_ID),
  appId: read(env.VITE_FIREBASE_APP_ID),
  databaseURL: read(env.VITE_FIREBASE_DATABASE_URL),
};

/** The fields we genuinely need before talking to Firebase. */
const REQUIRED: ReadonlyArray<keyof FirebaseEnv> = [
  'apiKey',
  'authDomain',
  'projectId',
  'appId',
  'databaseURL',
];

export const useEmulators = read(env.VITE_FIREBASE_USE_EMULATORS) === 'true';

const flagOn = read(env.VITE_FIREBASE_ENABLED) === 'true';
const configComplete = REQUIRED.every((k) => firebaseConfig[k].length > 0);

/** True only when the flag is on AND the required config is present. */
export const firebaseEnabled: boolean = flagOn && configComplete;

if (flagOn && !configComplete) {
  // eslint-disable-next-line no-console
  console.warn(
    '[services] VITE_FIREBASE_ENABLED=true but config is incomplete — ' +
      'falling back to the local (offline) backend. Missing: ' +
      REQUIRED.filter((k) => firebaseConfig[k].length === 0).join(', '),
  );
}
